import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { ADMIN_ACCESS_TOKEN_KEY } from "services/admin/adminApi";
import { loginAdmin, logoutAdmin, meAdmin } from "services/admin/adminAuth.service";
import { AdminUser } from "services/admin/adminAuth.types";

export type AdminAuthCtx = {
  loading: boolean;
  isAuthenticated: boolean;
  admin: AdminUser | null;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
};

export const AdminAuthContext = createContext<AdminAuthCtx | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const refreshAdmin = useCallback(async () => {
    try {
      const data = await meAdmin();
      if (data?.admin) {
        setAdmin(data.admin);
        setIsAuthenticated(true);
      } else {
        setAdmin(null);
        setIsAuthenticated(false);
      }
    } catch {
      setAdmin(null);
      setIsAuthenticated(false);
    }
  }, []);

  const login = useCallback(
    async (email: string, senha: string) => {
      setLoading(true);
      try {
        const data = await loginAdmin(email, senha);
        if (data?.accessToken) {
          localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, data.accessToken);
        }
        setAdmin(data.admin);
        setIsAuthenticated(true);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await logoutAdmin();
    } finally {
      localStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
      setAdmin(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await refreshAdmin();
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshAdmin]);

  const value = useMemo<AdminAuthCtx>(
    () => ({ loading, isAuthenticated, admin, login, logout, refreshAdmin }),
    [loading, isAuthenticated, admin, login, logout, refreshAdmin]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
